import React, {
  useRef,
  useState,
  ReactNode,
  useEffect,
  useCallback,
} from "react";
import {useParams} from "react-router-dom";
import {cx} from "@emotion/css";
import html2canvas from "html2canvas";
import axios from "axios";
import {UserPermissionEnum} from "../../utils/types/user";

import {UseIsInViewport} from "../../hooks/useIsInViewport";
import {
  AnyDataPath,
  Facility,
  IFromTo,
} from "../../store/reporting/reporting.common";
import {BLOCK_PERMISSIONS} from "../../pages/page-reporting/permissions.helpers";
import {createActions} from "../../store/reporting-reports/reporting-reports.actions";
import {
  isReportsDataPath,
  PER_PAGE,
  ReportsDataPath,
} from "../../store/reporting-reports/reporting-reports.common";
import {dispatch} from "../../store";
import {RequiredUserPermissionsSome} from "../admin-user-permissions/RequiredUserPermissionsSome";
import {TemporaryHideChart} from "../../pages/page-reporting/TemporaryHideChart";
import {
  ReportContextConsumer,
  useReportContext,
} from "../../pages/page-reporting/ReportContext";
import {FileExtensionItem} from "./file-export-dropdown-select/FileExportDropdownSelect";
import {Header} from "./Header";
import {
  downloadCSV,
  downloadPDF,
  downloadXLSX,
  FileExtension,
} from "./downloadExport";
import {Column, ReportWrapper, ReportBody} from "./ReportingBlock.styles";

import "./ReportingBlock.scss";

export type ReportingBlockSetters = Readonly<{
  setExportDataPath: (exportDataPath: AnyDataPath) => void;
  exportCanvasToPDF: (canvas: HTMLCanvasElement) => void;
  half?: boolean;
  /** Renders tables and charts at full height with paging instead of as a capped preview. */
  isFullReport?: boolean;
  enabled: boolean;
}>;

type Props = Readonly<{
  chartId: string;
  children: (reportingBlockSetters: ReportingBlockSetters) => ReactNode;
  dateRange: IFromTo;
  facilities: Facility[];
  half?: boolean;
  setPageLoading?: (state: boolean) => void;
  permissionInChart?: ReadonlyArray<UserPermissionEnum>;
  isDynamicCheckbox?: boolean;
  hideCardExport?: boolean;
}>;

type ReportingBlockContentProps = {
  inViewRef: React.Ref<HTMLDivElement>;
  wasInViewport: boolean;
  bodyRef: React.RefObject<HTMLDivElement>;
  isFullReport: boolean;
  half: boolean | undefined;
  disableHalf: boolean;
  getExportDataPath: () => AnyDataPath;
  onExport: (item: FileExtensionItem) => void;
  hideCardExport: boolean;
  children: Props["children"];
  setExportDataPath: (path: AnyDataPath) => void;
  exportCanvasToPDF: (canvas: HTMLCanvasElement) => void;
};

type ActionRowExportRegistrationProps = Readonly<{
  chartId: string;
  hideCardExport: boolean;
  exportDataPath: AnyDataPath | undefined;
  facilities: Facility[];
  getExportDataPath: () => AnyDataPath;
  onExport: (item: FileExtensionItem) => void;
}>;

const ActionRowExportRegistration = ({
  chartId,
  hideCardExport,
  exportDataPath,
  facilities,
  getExportDataPath,
  onExport,
}: ActionRowExportRegistrationProps) => {
  const {registerExportControls, clearExportControls} = useReportContext();

  useEffect(() => {
    if (!hideCardExport) {
      return undefined;
    }

    const dataPath = getExportDataPath();

    registerExportControls(
      {onExport, dataPath},
      {ownerId: chartId, mode: "replace"},
    );

    return () => clearExportControls(chartId);
  }, [
    hideCardExport,
    chartId,
    exportDataPath,
    facilities,
    onExport,
    registerExportControls,
    clearExportControls,
  ]);

  return null;
};

const ReportingBlockContent = ({
  inViewRef,
  wasInViewport,
  bodyRef,
  isFullReport,
  half,
  disableHalf,
  getExportDataPath,
  onExport,
  hideCardExport,
  children,
  setExportDataPath,
  exportCanvasToPDF,
}: ReportingBlockContentProps) => (
  <Column className={cx({half: half && !disableHalf})}>
    <ReportWrapper>
      {!hideCardExport && (
        <Header dataPath={getExportDataPath()} onExport={onExport} />
      )}
      <ReportBody>
        <div ref={inViewRef}>
          <div ref={bodyRef}>
            {(children as any)({
              setExportDataPath,
              exportCanvasToPDF,
              isFullReport,
              half,
              enabled: wasInViewport,
            })}
          </div>
        </div>
      </ReportBody>
    </ReportWrapper>
  </Column>
);

export const ReportingBlockRenderer = ({
  chartId,
  children,
  dateRange,
  facilities,
  half,
  setPageLoading,
  permissionInChart = [],
  isDynamicCheckbox = false,
  hideCardExport = false,
}: Props) => {
  const {sectionId} = useParams<{sectionId?: string}>();

  const [exportDataPath, setExportDataPath] = useState<
    AnyDataPath | undefined
  >();
  const [disableHalf, setDisableHalf] = useState(false);

  const bodyRef = useRef<HTMLDivElement>(null);

  // Refs prevent stale closures in async callbacks (getCanvasFromReport, downloadPDF)
  const setPageLoadingRef = useRef(setPageLoading);
  const facilitiesRef = useRef(facilities);
  const exportDataPathRef = useRef<AnyDataPath | undefined>();
  setPageLoadingRef.current = setPageLoading;
  facilitiesRef.current = facilities;
  exportDataPathRef.current = exportDataPath;

  const getExportDataPath = () =>
    ({
      ...exportDataPathRef.current,
      query: {
        ...exportDataPathRef.current?.query,
        facilities: facilitiesRef.current,
      },
    } as AnyDataPath);

  const getCanvasFromReport = () => {
    setDisableHalf(true);
    const exportElement = bodyRef.current as HTMLElement;
    const parentContainer = exportElement.parentElement as HTMLElement;
    const button = parentContainer.getElementsByTagName("button")[0];

    if (button) {
      button.style.display = "none";
    }

    parentContainer.style.overflow = "hidden";
    parentContainer.style.width = `${exportElement.clientWidth}px`;
    exportElement.style.width = "1280px";

    return new Promise((resolve, reject) => {
      setTimeout(() => {
        // setTimeout is used to wait for rerendering graphs to full width
        html2canvas(exportElement, {
          windowWidth: 1920,
          windowHeight: 1080,
          scale: 2,
          logging: false,
          onclone: document => {
            setDisableHalf(false);
            exportElement.style.width = "";
            parentContainer.style.width = "";
            parentContainer.style.overflow = "";
            // this extra style expands scrollable content
            const head = document.head;
            const style = document.createElement("style");

            style.type = "text/css";
            style.appendChild(
              document.createTextNode(
                ".chart-header { align-items: stretch !important; }" +
                  ".graph-bar-wrapper { max-height: none !important; } " +
                  ".list-table-reporting-wrapper { max-height: none !important; } " +
                  ".list-table-reporting tbody { height: auto !important; }" +
                  ".list-table-reporting-full+a { display: none; }" +
                  ".reporting-block__body { padding: 0 !important; }" +
                  ".fakeCheck { font-weight: bold; font-size: 11px; }" +
                  ".ellipsis span { display: inline-block !important; overflow: visible !important; white-space: normal !important; }" +
                  ".graph-bar tbody { height: auto !important; }" +
                  ".additional-data { max-height: none !important; }",
              ),
            );
            head.appendChild(style);

            const allChecks = document.querySelectorAll(
              ".list-table-reporting td .icon-check",
            );

            allChecks.forEach(item => {
              const fakeCheck = document.createElement("span");

              fakeCheck.innerHTML = "&#10004;";
              fakeCheck.className = "fakeCheck";
              item.parentNode.replaceChild(fakeCheck, item);
            });
          },
        })
          .then((canvas: any) => {
            resolve(canvas);
            if (button) {
              button.style.display = "flex";
            }
          })
          .catch((error: any) => {
            reject(error);
            if (button) {
              button.style.display = "flex";
            }
          });
      }, 0);
    });
  };

  const exportCanvasToPDF = (canvas: HTMLCanvasElement) => {
    downloadPDF(canvas, getExportDataPath()).then(() => {
      setPageLoadingRef.current?.(false);
    });
  };

  const onPDFExport = () => {
    const exportDataPathValue = getExportDataPath();
    const actions = createActions(exportDataPathValue as ReportsDataPath);

    setPageLoadingRef.current?.(true);

    if (isReportsDataPath(exportDataPathValue)) {
      /**
       * If we have full report with load more in "Reports page" we need to load all data to DOM.
       */
      dispatch(actions.loadAllForPDF()).then((oldPage: number) => {
        getCanvasFromReport().then(canvas => {
          const cancelTokenSource = axios.CancelToken.source();

          dispatch(
            actions.fetchReports(
              cancelTokenSource,
              0,
              (oldPage + 1) * PER_PAGE,
              true,
            ),
          );
          downloadPDF(canvas as HTMLCanvasElement, getExportDataPath()).then(
            () => {
              setPageLoadingRef.current?.(false);
            },
          );
        });
      });
    } else {
      getCanvasFromReport().then(canvas => {
        downloadPDF(canvas as HTMLCanvasElement, getExportDataPath()).then(
          () => {
            setPageLoadingRef.current?.(false);
          },
        );
      });
    }
  };

  const onExport = useCallback(
    ({id: extension}: FileExtensionItem) => {
      if (extension === FileExtension.PDF) {
        onPDFExport();
      } else if (extension === FileExtension.CSV) {
        downloadCSV(getExportDataPath());
      } else if (extension === FileExtension.XLSX) {
        downloadXLSX(getExportDataPath());
      }
    },
    [exportDataPath, facilities],
  );

  /**
   * A report on its own page has the whole viewport to work with, so tables and
   * charts render at full height with paging instead of as a capped preview.
   */
  const onReportPage = Boolean(sectionId);

  return (
    <RequiredUserPermissionsSome
      permissions={
        isDynamicCheckbox
          ? BLOCK_PERMISSIONS.CHECKLIST_ITEM_REPORT_ID
          : BLOCK_PERMISSIONS[chartId] || permissionInChart
      }
    >
      <ActionRowExportRegistration
        chartId={chartId}
        hideCardExport={hideCardExport}
        exportDataPath={exportDataPath}
        facilities={facilities}
        getExportDataPath={getExportDataPath}
        onExport={onExport}
      />
      <UseIsInViewport>
        {({ref, wasInViewport}) => (
          <ReportingBlockContent
            inViewRef={ref}
            wasInViewport={wasInViewport}
            bodyRef={bodyRef}
            isFullReport={onReportPage}
            half={half}
            disableHalf={disableHalf}
            getExportDataPath={getExportDataPath}
            onExport={onExport}
            hideCardExport={hideCardExport}
            setExportDataPath={setExportDataPath}
            exportCanvasToPDF={exportCanvasToPDF}
          >
            {children}
          </ReportingBlockContent>
        )}
      </UseIsInViewport>
    </RequiredUserPermissionsSome>
  );
};

export const ReportingBlock = ReportingBlockRenderer;

// TODO: remove once the ReportingBlock consumes the ReportContext
export const ConnectedReportingBlock = (
  props: Omit<Props, "dateRange" | "facilities" | "setPageLoading">,
) => (
  <TemporaryHideChart chartIds={[props.chartId]}>
    <ReportContextConsumer>
      {({baseDateRange, activeFacilitiesIds, setPageLoading}) => (
        <ReportingBlock
          dateRange={baseDateRange}
          facilities={activeFacilitiesIds}
          setPageLoading={setPageLoading}
          {...props}
        />
      )}
    </ReportContextConsumer>
  </TemporaryHideChart>
);
